import { CapacitorHttp } from '@capacitor/core';
import { getApiLang, i18nt } from '@/i18n';
import { showLoading, toPage } from '@/utils';
import { resolveApiErrorToastMessage } from '@/utils/request/apiErrorMessage';
import { storage } from '@/plugins/ionic-storage';
import { useTokenStore, useUserStore } from '@/store';
import { showToast, ToastWrapperInstance, showConfirmDialog } from 'vant';
import { refreshTokenAction as refresh } from './refreshToken';
import { disconnectWs } from '@/views/conversation/socket';
import { useChatHistoryStore } from '@/store/chatHistory';

const baseURL = import.meta.env.VITE_APP_BASE_URL;
console.log('进到app=========================', baseURL);

let isLoading = 0;
let loadingInstance: ToastWrapperInstance | undefined;

function buildUrl(url: string) {
  if (!url) return url;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `${baseURL}${url}`;
}

const errHandle = (error: any) => {
  if (isLoading == 1) {
    loadingInstance && loadingInstance.close();
    loadingInstance = undefined;
  }
  isLoading--;
  const status = error?.status || error?.statusCode || 0;
  if (status >= 500) {
    showToast(i18nt('wlcwqshzs'));
  }
  return Promise.reject(error);
};

const logoutAndRedirect = async (
  msg?: string,
  msgKey?: string | null,
) => {
  const { clearUser } = useUserStore();
  const chatHistoryStore = useChatHistoryStore();
  const query: { [key: string]: string } = {};
  if (location.pathname == '/grass' && location.search) {
    const params = location.search.split('?')[1];
    params.split('&').forEach((item) => {
      const [key, value] = item.split('=');
      query[key] = value;
    });
  }
  if (location.pathname !== '/login') {
    showToast(resolveApiErrorToastMessage(msgKey, msg, 'dqdlygq'));
    toPage(`/login`, false, query);
    try {
      disconnectWs();
      chatHistoryStore.resetSession();
      await clearUser();
    } catch (err) {
      toPage(`/login`, false, query);
    }
  }
};

const successHandle = async (response: any, config: any) => {
  if (isLoading == 1) {
    loadingInstance && loadingInstance.close();
    loadingInstance = undefined;
  }
  isLoading--;

  // CapacitorHttp 返回的响应结构：{ data, status, headers, url }
  // 如果 Content-Type 是 JSON 或未指定 responseType，response.data 已自动解析为对象
  // 如果指定了 responseType: 'blob' / 'text' / 'arraybuffer'，response.data 是原始数据
  let respData: any =
    response && response.data !== undefined ? response.data : response;

  // 仅当指定了 'text' responseType 且收到字符串时，才手动解析 JSON
  if (config?.responseType === 'text' && typeof respData === 'string') {
    try {
      respData = JSON.parse(respData);
    } catch (e) {
      // 如果无法解析，保持原字符串
    }
  }

  // 如果要求 blob 类型，处理二进制数据
  if (config?.responseType === 'blob' && respData) {
    try {
      if (respData instanceof ArrayBuffer) {
        const blob = new Blob([respData]);
        return Promise.resolve(URL.createObjectURL(blob));
      }
      // 如果已经是 Blob，直接返回
      if (respData instanceof Blob) {
        return Promise.resolve(URL.createObjectURL(respData));
      }
      return Promise.resolve(respData);
    } catch (e) {
      // fallthrough
    }
  }

  const data = respData && 'data' in respData ? respData.data : respData;
  const code = respData && respData.code;
  const msg = respData && respData.msg;
  const msgKey = respData && respData.msgKey;

  if (code === 1003000003) {
    showConfirmDialog({
      title: i18nt('yebz'),
      message: i18nt('ndyebzqcz'),
      confirmButtonText: i18nt('qcz_1'),
      cancelButtonText: i18nt('xczs'),
    }).then(() => {
      toPage('/wallet');
    });
    return Promise.reject(response);
  }

  if (code == 0) {
    return Promise.resolve(data);
  }

  if (code === 401) {
    logoutAndRedirect(msg, msgKey);
    return Promise.reject(response);
  }

  if (code) {
    if (!config?.customTip) {
      showToast(resolveApiErrorToastMessage(msgKey, msg));
    }
    return Promise.reject(response);
  }

  return Promise.resolve(respData);
};

async function makeRequest(opts: {
  url: string;
  method?: string;
  params?: any;
  data?: any;
  responseType?: string;
  headers?: Record<string, any>;
  customTip?: boolean;
  hideLoading?: boolean;
  [k: string]: any;
}) {
  if (isLoading === 0 && !opts.hideLoading) {
    loadingInstance = showLoading({ delay: 3000 }) as any;
  }
  isLoading++;

  let {
    token: { accessToken },
  } = useTokenStore();
  accessToken = accessToken || (await storage.get('accessToken'));

  // const headers: Record<string, any> = opts.headers || {};

  const url = buildUrl(opts.url);
  const method = (opts.method || 'get').toUpperCase() as
    | 'GET'
    | 'POST'
    | 'PUT'
    | 'DELETE'
    | 'PATCH';

  try {
    const requestOptions: any = {
      url,
      headers: {
        'Content-Type': 'application/json',
        lang: getApiLang(),
        ...(opts.headers || {}),
        ...(accessToken ? { Authorization: `${accessToken}` } : {}),
      },
    };

    // CapacitorHttp 会自动处理 params 和 data
    if (opts.params) {
      requestOptions.params = opts.params;
    }
    console.log('opts.data--------', opts.data);
    if (opts.data) {
      requestOptions.data = opts.data;
    }

    // 设置 responseType
    if (opts.responseType === 'blob') {
      requestOptions.responseType = 'blob';
    }

    if (opts.onUploadProgress) {
      requestOptions.uploadProgressCallback = (progress) => {
        const percent = Math.floor(
          (progress.loaded / progress.total) * 100
        )
        opts.onUploadProgress({ percent });
      }
    }

    // 根据 HTTP 方法调用相应的 CapacitorHttp 方法（官方推荐做法）
    let result: any;
    switch (method) {
      case 'GET':
        result = await CapacitorHttp.get(requestOptions);
        break;
      case 'POST':
        console.log('post----', JSON.stringify(requestOptions));
        result = await CapacitorHttp.post(requestOptions);
        break;
      case 'PUT':
        result = await CapacitorHttp.put(requestOptions);
        break;
      case 'DELETE':
        result = await CapacitorHttp.delete(requestOptions);
        break;
      case 'PATCH':
        result = await CapacitorHttp.patch(requestOptions);
        break;
      default:
        // 不应该走到这里（method 已被限制为有效值）
        throw new Error(`Unsupported HTTP method: ${method}`);
    }

    return await successHandle(result, opts);
  } catch (error: any) {
    return errHandle(error);
  }
}

export const postRequest = (url: string, data = {}, opts = {}) => {
  return makeRequest({ url, method: 'post', data, ...opts });
};

// 获取二进制流
export const getBlobRequest = (url: string, data = {}) => {
  return makeRequest({
    url,
    method: 'get',
    responseType: 'blob',
    params: data,
  });
};

export const getRequest = (url: string, data = {}, opts = {}) => {
  return makeRequest({ url, method: 'get', params: data, ...opts });
};

export const putRequest = (url: string, data = {}, opts = {}) => {
  return makeRequest({ url, method: 'put', data, ...opts });
};

export const delRequest = (url: string, data = {}) => {
  // DELETE 请求应使用 params 而不是 data（某些 API 需要 data，但 CapacitorHttp 推荐用 params）
  return makeRequest({ url, method: 'delete', params: data });
};

const appHttp = {
  postRequest,
  getRequest,
  putRequest,
  delRequest,
  getBlobRequest,
};
export default appHttp;
